import { makeNamespacedLog } from '@sb/log';

import type { RoutineDocument } from './diffRoutineDocument';
import type { EditDirection, FieldChange } from './types';
import { ABSENT } from './types';

const log = makeNamespacedLog('applyFieldChanges');

/**
 * Apply field changes to a document in memory, the way the server applies the
 * patch `changesToPatch` builds from them: `after` values for `redo`, `before`
 * values for `undo`, an absent side removes the field.
 *
 * Paths are the ones `diffRoutineDocument` produces — a top-level field, or one
 * key of a per-key field such as `stepConfigurations.<stepID>`.
 *
 * Whole fields only: an entry's item edits are replayed against the live
 * document at press time and are not reproduced here. Used to reconstruct the
 * two sides of an entry from its changes alone — `applyFieldChanges({}, changes,
 * 'undo')` is the partial document the entry started from — which is all the
 * describers and the item-scoped diff need.
 */
export function applyFieldChanges(
  document: RoutineDocument,
  changes: FieldChange[],
  direction: EditDirection,
): RoutineDocument {
  const next: Record<string, unknown> = { ...document };

  for (const change of changes) {
    const value = direction === 'undo' ? change.before : change.after;
    const [field, key, ...rest] = change.path.split('.');

    if (key === undefined) {
      if (value === ABSENT) {
        delete next[field];
      } else {
        next[field] = value;
      }
    } else if (rest.length === 0) {
      const record = { ...((next[field] as Record<string, unknown>) ?? {}) };

      if (value === ABSENT) {
        delete record[key];
      } else {
        record[key] = value;
      }

      next[field] = record;
    } else {
      // Deeper paths are not something the diff produces; leave them alone
      // rather than guess at a shape. Loudly: if the diff ever grows finer, a
      // silently skipped change would reconstruct an incomplete document.
      log.warn('path.unsupported', 'Skipping a field change at a nested path', {
        path: change.path,
      });
    }
  }

  return next as RoutineDocument;
}
